import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';

function DonutChart({ data = [], size = 160, innerRatio = 0.6, label = '' }) {
  const [hovered, setHovered] = useState(null);
  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 8;
  const innerR = outerR * innerRatio;
  const total = data.reduce((s, d) => s + d.value, 0) || 1;

  let currentAngle = -Math.PI / 2;

  const segments = data.map((item, i) => {
    const fraction = item.value / total;
    const angle = fraction * 2 * Math.PI;
    const startAngle = currentAngle;
    currentAngle += angle;
    const endAngle = currentAngle;

    const x1o = cx + outerR * Math.cos(startAngle);
    const y1o = cy + outerR * Math.sin(startAngle);
    const x2o = cx + outerR * Math.cos(endAngle);
    const y2o = cy + outerR * Math.sin(endAngle);

    const x1i = cx + innerR * Math.cos(endAngle);
    const y1i = cy + innerR * Math.sin(endAngle);
    const x2i = cx + innerR * Math.cos(startAngle);
    const y2i = cy + innerR * Math.sin(startAngle);

    const largeArc = angle > Math.PI ? 1 : 0;

    const d = [
      `M ${x1o} ${y1o}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2o} ${y2o}`,
      `L ${x1i} ${y1i}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 0 ${x2i} ${y2i}`,
      'Z',
    ].join(' ');

    return { d, color: item.color, label: item.label, value: item.value, fraction, i };
  });

  const activeItem = hovered !== null ? data[hovered] : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size}>
          {segments.map((seg) => (
            <motion.path
              key={seg.i}
              d={seg.d}
              fill={seg.color}
              opacity={hovered === null ? 0.9 : hovered === seg.i ? 1 : 0.5}
              onMouseEnter={() => setHovered(seg.i)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: hovered === null ? 0.9 : hovered === seg.i ? 1 : 0.5 }}
              transition={{ duration: 0.6, delay: seg.i * 0.1 }}
            />
          ))}
        </svg>
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          {activeItem ? (
            <>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: activeItem.color }}>
                {activeItem.percentage ?? activeItem.value}%
              </Typography>
              <Typography sx={{ fontSize: '0.6rem', color: '#6b7280', textAlign: 'center', px: 1 }}>
                {activeItem.label} ({activeItem.value})
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#374151' }}>{label}</Typography>
            </>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
        {data.map((item, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: item.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.78rem', color: '#374151', fontWeight: 500 }}>{item.label}</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af', ml: 'auto', pl: 1 }}>
              {item.value} ({item.percentage ?? item.value}%)
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default DonutChart;
